import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  IconButton,
  Pagination,
  Box,
  Button,
  Container,
  Breadcrumbs,
  Link as MUILink,
} from "@mui/material";
import { Link } from "react-router-dom";
import moment from "moment";
import { Delete } from "@mui/icons-material";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import { message, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../Components/PageTitle";
import {
  getEventsNews,
  deleteEventNews,
  createEventNews,
} from "../../redux/actions/admin/eventsAndNewsAction";
import EditEventsNews from "./EditEventsNews";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const EventsNewsList = () => {
  const dispatch = useDispatch();
  const { eventsNews, loading, error } = useSelector(
    (state) => state.eventsNews
  );
  const [value, setValue] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filteredEventsNews, setFilteredEventsNews] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(startDate);

  const itemsPerPage = 5;

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  useEffect(() => {
    dispatch(getEventsNews());
  }, [dispatch]);

  useEffect(() => {
    setFilteredEventsNews(eventsNews);
  }, [eventsNews]);

  // Function to refresh the Events & News data
  const refreshData = () => {
    dispatch(getEventsNews()); // Refresh data after update
  };

  // Get Events & News
  useEffect(() => {
    if (eventsNews?.data) {
      const processedData = eventsNews.data.map((item) => ({
        id: item._id,
        title: item.title,
        description: item.description,
        startDate: item.startDate,
        endDate: item.endDate,
      }));

      setFilteredEventsNews(processedData);
    }
  }, [eventsNews]);

  // Handle Search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);

    if (!value) {
      setFilteredEventsNews(
        eventsNews.data.map((item) => ({
          id: item._id,
          title: item.title,
          description: item.description,
          startDate: item.startDate,
          endDate: item.endDate,
        }))
      );
      return;
    }

    const filteredData = eventsNews.data
      .filter((item) => item.title.toLowerCase().includes(value))
      .map((item) => ({
        id: item._id,
        title: item.title,
        description: item.description,
        startDate: item.startDate,
        endDate: item.endDate,
      }));

    setFilteredEventsNews(filteredData);
  };

  // Handle Pagination
  const handlePageChange = (_, value) => setPage(value);
  const paginatedItems = filteredEventsNews?.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Handle Delete Api Events & News
  const { confirm } = Modal;
  const handleDelete = async (id) => {
    confirm({
      title: "Are you sure you want to delete this Event & News?",
      content: "Once deleted, this event & news cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        await dispatch(deleteEventNews(id));
        await dispatch(getEventsNews());
      },
    });
  };

  const eventsNewsStartDate = moment(startDate).format("MM/DD/YYYY");
  const eventsNewsEndDate = moment(endDate).format("MM/DD/YYYY");

  // Handle Api Events & News
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) {
      message.error("Please enter a events & news");
      return;
    }
    if (!startDate) {
      message.error("Please enter a Start Date Events & News");
      return;
    }
    if (!endDate) {
      message.error("Please enter a End Date Events & News");
      return;
    }
    const eventData = {
      title,
      description,
      startDate: eventsNewsStartDate,
      endDate: eventsNewsEndDate,
    };
    await dispatch(createEventNews(eventData));
    await dispatch(getEventsNews());
    setTitle(null);
    setDescription(null);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <>
      <PageTitle
        title="Manage Events & News - Admin Panel | Shree Mangalam School"
        description="Create, edit, and manage school events and news through the Admin Panel of Shree Mangalam School. Keep students and parents informed with timely updates."
        url="/admin/events-news"
        keywords="admin events, admin news management, Shree Manglam announcements, school updates, post school events, admin panel news section"
      />
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Events & News
      </Typography>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Events & News</Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Events & News List" {...TabPanel(0)} />
          <Tab label="Add Events & News" {...TabPanel(1)} />
        </Tabs>
      </Box>
      {/* Events & News List */}

      <CustomTabPanel value={value} index={0}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Events & News List
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Events & News"
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            mt: 2,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Card sx={{ width: "100%", borderRadius: 4 }}>
          <CardContent>
            <Grid container spacing={2}>
              {paginatedItems?.map((item, index) => (
                <Grid
                  container
                  item
                  key={item.id}
                  alignItems="center"
                  justifyContent="space-between"
                  borderBottom={1}
                  borderColor="divider"
                  // p={1}
                >
                  <Grid item xs={2} md={1}>
                    {/* <IconButton aria-label="number" sx={{fontSize: "16px",color: "#000",}}>{index+1}</IconButton> */}
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Delete sx={{ color: "red" }} />
                    </IconButton>
                    <EditEventsNews
                      id={item.id}
                      title={item.title}
                      description={item.description}
                      startDate={item.startDate}
                      endDate={item.endDate}
                      refreshData={refreshData}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={10}
                    md={11}
                    textAlign="left"
                    alignItems="center"
                    justifyContent="space-between"
                    p={1}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      <div dangerouslySetInnerHTML={{ __html: item.title }} />
                    </Typography>
                    <Typography variant="body2">
                      <div
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      {`Start Date: ${item.startDate} | End Date: ${item.endDate}`}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Pagination
              count={Math.ceil(filteredEventsNews.length / itemsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              sx={{ mt: 2, display: "flex", justifyContent: "center" }}
            />
          </CardContent>
        </Card>
      </CustomTabPanel>

      {/* Create a New Add Events & News */}
      <CustomTabPanel value={value} index={1} maxWidth>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Create a new Events & News
        </Typography>
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="form_style border-style"
          maxWidth
        >
          <h4>Title</h4>
          <ReactQuill
            value={title}
            onChange={setTitle}
            placeholder="Add Events & News Title"
            style={{
              // height: "200px",
              marginBottom: "30px",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
            }}
          />
          <h4>Description</h4>
          <ReactQuill
            value={description}
            onChange={setDescription}
            placeholder="Add Events & News Description"
            style={{
              // height: "200px",
              marginBottom: "30px",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
            }}
          />
          <Box className="Date">
            <Box sx={{ mt: 3 }}>
              <h4>Start Date</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="Start Date"
                selected={startDate}
                onChange={handleStartDateChange}
                minDate={new Date()}
                maxDate={endDate}
                value={startDate}
              />
            </Box>
            <Box sx={{ mt: 3, mb: 3 }}>
              <h4>End Date</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="End Date"
                selected={endDate}
                onChange={handleEndDateChange}
                minDate={(new Date() && startDate) || new Date()}
                value={endDate}
                maxDate={endDate}
              />
            </Box>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="courses_desc"
            sx={{
              mt: 3,
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              letterSpacing: ".1rem",
            }}
          >
            Add Events & News
          </Button>
        </Container>
      </CustomTabPanel>
    </>
  );
};

export default EventsNewsList;
